import {
  BrowserAuthError,
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
} from '@azure/msal-browser';

const TENANT_ID = import.meta.env.VITE_AZURE_TENANT_ID ?? '';
const CLIENT_ID = import.meta.env.VITE_AZURE_CLIENT_ID ?? '';
const API_SCOPE = import.meta.env.VITE_AZURE_API_SCOPE ?? '';
// Un hôte FQDN absolu (« https://hub.altyor.solutions. », point final) est une
// origine valide pour le navigateur — le DNS résout, Hosting sert l'app — mais elle
// n'est enregistrée nulle part dans Entra : le login part en AADSTS50011. On retire
// le point avant de passer l'origine à MSAL, qui ramènera l'utilisateur sur l'origine
// canonique après authentification. Vu en prod le 2026-09-18 (autocomplétion).
export const canonicalOrigin = (origin: string) => origin.replace(/\.(?=:|$)/, '');

const REDIRECT_URI =
  import.meta.env.VITE_AZURE_REDIRECT_URI || canonicalOrigin(window.location.origin);

// ponytail: pas de flag `msalConfigured` ici — personne ne le lisait, donc il ne
// protégeait rien. Le garde-fou est au build (apps/web/vite.config.ts), qui échoue
// si TENANT_ID / CLIENT_ID / API_SCOPE manquent.

const pca = new PublicClientApplication({
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    redirectUri: REDIRECT_URI,
  },
  cache: { cacheLocation: 'sessionStorage' }, // ponytail: durcissement XSS (spec §2.5)
});

const request = { scopes: [API_SCOPE] };

function firstAccount(): AccountInfo | null {
  return pca.getAllAccounts()[0] ?? null;
}

export const msalClient = {
  async ready() {
    await pca.initialize(); // requis par msal-browser v3 avant tout autre appel
  },
  async handleRedirect() {
    await pca.handleRedirectPromise(); // consomme le retour de loginRedirect
  },
  hasAccount() {
    return firstAccount() !== null;
  },
  async login() {
    await pca.loginRedirect(request);
  },
  async logout() {
    await pca.logoutRedirect();
  },
  async acquireToken() {
    const account = firstAccount();
    if (!account) throw new Error('not signed in');
    try {
      const r = await pca.acquireTokenSilent({ ...request, account });
      return r.accessToken;
    } catch (e) {
      // Rediriger fait quitter la page et perdre la saisie en cours (#66) :
      // seulement quand une interaction est réellement nécessaire. Une panne
      // passagère (réseau…) remonte comme une erreur ordinaire de la requête.
      const needsInteraction =
        e instanceof InteractionRequiredAuthError ||
        (e instanceof BrowserAuthError && e.errorCode === 'monitor_window_timeout');
      if (!needsInteraction) throw e;
      await pca.acquireTokenRedirect(request); // navigue away
      throw new Error('redirecting for token');
    }
  },
};
